import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useCallback } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { FormStrucItem } from 'src/lib/types/types';
import { NonRequired } from 'src/lib/validation/ValidationTypes';
import { checkReadOnlyInUeberarbeitung } from 'src/utils/helper';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = Schema.attributes;

const KombinationAnderenBeihilfen: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const getTitle = useCallback((eachBeihilfe: any, index: number) => {
		const hilfenGeberName = get(eachBeihilfe, 'Beihilfegeber', null);

		if (hilfenGeberName !== null && hilfenGeberName !== '') {
			return `${index + 1} - ${hilfenGeberName}`;
		} else {
			return `${index + 1} - Beihilfe`;
		}
	}, []);

	const handleAddBeihilfen = () => {
		const appCopy = cloneDeep(application);
		const allBeihilfeArt_Length = get(appCopy, P.Deminimis_Kombination.path, []).length;

		const key = `${P.Deminimis_Kombination.path}.${allBeihilfeArt_Length}.Unternehmen`;
		const value = '';

		set(appCopy, key, value);
		saveApplication(false, appCopy);
	};
	const handleDeleteBeihilfen = (index: number) => {
		let appCopy = cloneDeep(application);
		const allBeihilfeArt = get(appCopy, P.Deminimis_Kombination.path, []);
		set(
			appCopy,
			P.Deminimis_Kombination.path,
			allBeihilfeArt.filter((_: any, i: number) => i !== index),
		);
		saveApplication(false, appCopy);
	};

	return (
		<>
			<Typography variant='h3'>
				Angaben zur <u>Kombination</u> mit anderen Beihilfen
			</Typography>
			<Box sx={{ mt: '1rem' }}>
				<MUIAlert
					message={
						<>
							<strong>Einzutragen sind Förderungen, die auf Grundlage der AGVO oder genehmigten Beihilferegelungen gewährt werden.</strong>{' '}
							Bitte ergänzen Sie hier die Eckdaten der zu kombinierenden Förderung. Sollten Sie hierzu Fragen haben oder Unterstützung
							benötigen, können Sie sich gerne über den Kontaktbutton rechts unter dem Statusmenü mit uns in Verbindung setzen.
						</>
					}
				/>
			</Box>
			<Box sx={{ mt: '2rem', mb: '1rem' }}>
				<CreateForm formStruc={formStruc()} formValues={application} updateFormValuesFunc={setApplicationProperty} readOnly={updatedReadOnly} />
			</Box>
			{get(application, P.Kombiniert_mit_Anderen_Beihilfen.path) === 'ja44Folgende' ?
				<>
					{get(application, P.Deminimis_Kombination.path, []).map((eachBeihilfe: any, index: number) => {
						return (
							<Box key={index} sx={{ mt: '2rem' }}>
								<MUIAccordionComponent
									summaryTitle={getTitle(eachBeihilfe, index)}
									onDelete={() => handleDeleteBeihilfen(index)}
									showDeleteButton={!updatedReadOnly}
								>
									<Grid2 spacing={6} container>
										<Grid2 sm={6}>
											<CreateForm
												formStruc={mapFormStruc(formStrucBeihilfe(application), index)}
												formValues={application}
												updateFormValuesFunc={setApplicationProperty}
												readOnly={updatedReadOnly}
											/>
										</Grid2>
										<Grid2 sm={6}>
											<CreateForm
												formStruc={mapFormStruc(formStrucBeihilfeSpalte2(application), index)}
												formValues={application}
												updateFormValuesFunc={setApplicationProperty}
												readOnly={updatedReadOnly}
											/>
										</Grid2>
									</Grid2>
								</MUIAccordionComponent>
							</Box>
						);
					})}
				</>
			:	null}
			{get(application, P.Kombiniert_mit_Anderen_Beihilfen.path) === 'ja44Folgende' ?
				<Stack alignItems={'flex-end'} sx={{ mt: '2rem' }}>
					<Button endIcon={<AddIcon />} onClick={handleAddBeihilfen} disabled={updatedReadOnly}>
						Weitere Beihilfe hinzufügen
					</Button>
				</Stack>
			:	null}
		</>
	);
};

export default KombinationAnderenBeihilfen;

// AW und Tabelle
// Die hier relevante De-minimis-Beihilfe wird mit weiteren staatlichen Förderungen für das gleiche Projekt / die gleichen förderfähigen Kosten kombiniert bzw. kumuliert

const formStruc = () => {
	const struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Kombiniert_mit_Anderen_Beihilfen,
					label: (
						<>
							Die hier beantragte De-miminis-Beihilfe wird mit weiteren staatlichen Förderungen für das gleiche Projekt{' '}
							<u>und die gleichen förderfähigen Kosten</u> kombiniert bzw. kumuliert:
						</>
					),
				},
			],
			userProps: { columnToDivide: 1 },
		},
	];

	return struc;
};
const formStrucBeihilfe = (application: any) => {
	let struc: FormStrucItem[] = [];

	if (get(application, P.Kombiniert_mit_Anderen_Beihilfen.path) === 'ja44Folgende') {
		struc.push(
			{
				fields: [
					{
						...P.Deminimis_Kombination.schema.attributes.Unternehmen,
						tooltip: 'Unternehmen oder Unternehmensverbund gemäß Art. 2 Abs. 2 VO 2023/2831',
					},
				],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
			{
				fields: [P.Deminimis_Kombination.schema.attributes.Datum_Des_Antrags],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
			{
				fields: [{ ...P.Deminimis_Kombination.schema.attributes.Hoehe_Foerderfaehigen_Kosten, format: 'currency' }],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
			{
				fields: [{ ...P.Deminimis_Kombination.schema.attributes.Hoehe_Beantragten_Foerderung, format: 'currency' }],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
			{
				fields: [
					{
						...P.Deminimis_Kombination.schema.attributes.Hoehe_Resultierenden_Beihilfewert,
						tooltip:
							'Die Information kann i.d.R. anhand der beihilferechtlichen Grundlagen einer Richtlinie oder eines Produktblattes der zu kombinierenden Beihilfe entnommen werden.',
						format: 'currency',
						validation: [{ validateFunction: NonRequired }],
					},
				],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
		);
	}

	return struc;
};
const formStrucBeihilfeSpalte2 = (application: any) => {
	let struc: FormStrucItem[] = [];

	if (get(application, P.Kombiniert_mit_Anderen_Beihilfen.path) === 'ja44Folgende') {
		struc.push({
			fields: [
				{
					...P.Deminimis_Kombination.schema.attributes.Beihilfegeber,
					tooltip: 'Aktenzeichen bitte ebenfalls angeben',
					maxCharLength: 2000,
				},
				{
					...P.Deminimis_Kombination.schema.attributes.Form_Der_Beihilfe,
					tooltip: 'Zuschuss, Darlehen, Bürgschaft etc.',
					maxCharLength: 2000,
				},
				{
					...P.Deminimis_Kombination.schema.attributes.Grundlage_Foerderung,
					tooltip: 'Grundlage der Förderung (z. B. Bezeichnung Förderrichtlinie, Ad-hoc Beihilfe, ...)',
				},
				{
					...P.Deminimis_Kombination.schema.attributes.Agvo_Freistellungstatbestand,
					validation: [{ validateFunction: NonRequired }],
					tooltip: 'zugrunde liegender AGVO-Freistellungstatbestand (Artikelnummer)',
				},
				{
					...P.Deminimis_Kombination.schema.attributes.Maximale_Beihilfeintensitaet,
					tooltip:
						'Die Beihilfeintensität (prozentuale Obergrenze) gibt an, wie viel Prozent der förderfähigen Kosten maximal durch öffentliche Mittel gedeckt werden dürfen (i.d.R. Förderquote). Die Angabe bezieht sich auf die betreffende, hier zu kombinierende Beihilfe.',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Deminimis_Kombination.schema.attributes.Beihilfehoechstbetrag,
					tooltip:
						'Der Beihilfehöchstbetrag ist die Obergrenze in Euro, bis zu der eine staatliche Förderung rechtlich erlaubt ist. Die Angabe bezieht sich auf die betreffende, hier zu kombinierende Beihilfe.',
					validation: [{ validateFunction: NonRequired }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		});
	}

	return struc;
};
const mapFormStruc = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Deminimis_Kombination.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Deminimis_Kombination.path.length),
				};
			}),
		};
	});
	return result;
};

export const getFormStrucKombinationAnderenBeihilfen = (application: any) => {
	let completeStruc: FormStrucItem[] = [];
	const allBeihilfen = get(application, P.Deminimis_Kombination.path, []);

	allBeihilfen.forEach((_beihilfe: any, index: number) => {
		const formStrucBeihilfeWithIndex = mapFormStruc(formStrucBeihilfe(application), index);
		const formStrucBeihilfeSpalte2WithIndex = mapFormStruc(formStrucBeihilfeSpalte2(application), index);

		completeStruc = concat(completeStruc, formStrucBeihilfeWithIndex, formStrucBeihilfeSpalte2WithIndex);
	});

	completeStruc = concat(completeStruc, formStruc());

	return completeStruc;
};
